/**
 * @fileoverview Match3 Leaderboard Component
 * @description Leaderboard for Match3 game scores
 */

import React, { useState, useEffect, useCallback } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Image,
  ActivityIndicator,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useThemeColors } from "@/providers/ThemeProvider";
import { supabase } from "@/lib/supabase/client";
import { useAuth } from "@/providers/AuthProvider";

interface LeaderboardEntry {
  rank: number;
  userId: string;
  userName: string;
  avatarUrl: string | null;
  bestScore: number;
}

interface Match3LeaderboardProps {
  mode: "free" | "weekend";
  weekendId?: string;
  maxEntries?: number;
  onUserPress?: (userId: string) => void;
}

/**
 * Leaderboard component for Match3 game
 */
export const Match3Leaderboard: React.FC<Match3LeaderboardProps> = ({
  mode,
  weekendId,
  maxEntries = 10,
  onUserPress,
}) => {
  const colors = useThemeColors();
  const { activeProfile } = useAuth();
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [myRank, setMyRank] = useState<number | null>(null);

  /**
   * Fetch leaderboard data
   */
  const fetchLeaderboard = useCallback(async () => {
    try {
      setLoading(true);

      const { data, error } = await supabase
        .from("user_game_stats")
        .select(`
          user_id,
          personal_best_score,
          stats,
          profiles:user_id (
            full_name,
            nickname,
            avatar_url
          )
        `)
        .eq("game_slug", "match_3")
        .eq("mode", mode)
        .not("personal_best_score", "is", null)
        .gt("personal_best_score", 0)
        .order("personal_best_score", { ascending: false })
        .limit(maxEntries);

      if (error) throw error;

      const leaderboard: LeaderboardEntry[] = (data || []).map((entry: any, index: number) => ({
        rank: index + 1,
        userId: entry.user_id,
        userName: entry.profiles?.nickname || entry.profiles?.full_name || "Onbekend",
        avatarUrl: entry.profiles?.avatar_url,
        bestScore: entry.personal_best_score || 0,
      }));

      setEntries(leaderboard);

      // Find current user's rank
      if (activeProfile?.id) {
        const userEntry = leaderboard.find(e => e.userId === activeProfile.id);
        setMyRank(userEntry?.rank || null);
      }
    } catch (error) {
      console.error("Error fetching Match3 leaderboard:", error);
    } finally {
      setLoading(false);
    }
  }, [mode, maxEntries, activeProfile?.id]);

  useEffect(() => {
    fetchLeaderboard();
  }, [fetchLeaderboard]);

  /**
   * Get initials from name
   */
  const getInitials = (name: string): string => {
    return name
      .split(" ")
      .map(n => n.charAt(0))
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  /**
   * Get rank medal color
   */
  const getRankColor = (rank: number): string => {
    switch (rank) {
      case 1: return "#FFD700"; // Gold
      case 2: return "#C0C0C0"; // Silver
      case 3: return "#CD7F32"; // Bronze
      default: return colors.textSecondary;
    }
  };

  if (loading) {
    return (
      <View style={[styles.container, { backgroundColor: colors.surface }]}>
        <ActivityIndicator color={colors.primary} />
      </View>
    );
  }

  if (entries.length === 0) {
    return (
      <View style={[styles.container, { backgroundColor: colors.surface }]}>
        <Ionicons name="trophy-outline" size={32} color={colors.textTertiary} />
        <Text style={[styles.emptyText, { color: colors.textSecondary }]}>
          Nog geen scores
        </Text>
        <Text style={[styles.emptySubtext, { color: colors.textTertiary }]}>
          Speel een potje om op de ranglijst te komen!
        </Text>
      </View>
    );
  }

  return (
    <View style={[styles.container, { backgroundColor: colors.surface }]}>
      {/* Header */}
      <View style={styles.header}>
        <Ionicons name="trophy" size={20} color={colors.warning} />
        <Text style={[styles.headerTitle, { color: colors.text }]}>
          {mode === "weekend" ? "Weekend Ranglijst" : "Ranglijst"}
        </Text>
        {mode === "weekend" && (
          <View style={[styles.badge, { backgroundColor: colors.primaryLight }]}>
            <Text style={[styles.badgeText, { color: colors.primary }]}>
              Familie Weekend
            </Text>
          </View>
        )}
      </View>

      {/* Entries */}
      {entries.map((entry, index) => {
        const isCurrentUser = entry.userId === activeProfile?.id;
        
        return (
          <TouchableOpacity
            key={entry.userId}
            style={[
              styles.entry,
              index > 0 && { borderTopWidth: 1, borderTopColor: colors.border },
              isCurrentUser && { backgroundColor: colors.primaryLight },
            ]}
            onPress={() => onUserPress?.(entry.userId)}
            activeOpacity={0.7}
            accessibilityRole="button"
            accessibilityLabel={`${entry.userName}, rank ${entry.rank}, score ${entry.bestScore}`}
          >
            {/* Rank */}
            <View style={styles.rankContainer}>
              {entry.rank <= 3 ? (
                <Ionicons
                  name="medal"
                  size={24}
                  color={getRankColor(entry.rank)}
                />
              ) : (
                <Text style={[styles.rankText, { color: colors.textSecondary }]}>
                  {entry.rank}
                </Text>
              )}
            </View>

            {/* Avatar */}
            <View
              style={[
                styles.avatar,
                { backgroundColor: colors.surfaceSecondary },
              ]}
            >
              {entry.avatarUrl ? (
                <Image
                  source={{ uri: entry.avatarUrl }}
                  style={styles.avatarImage}
                />
              ) : (
                <Text style={[styles.avatarText, { color: colors.primary }]}>
                  {getInitials(entry.userName)}
                </Text>
              )}
            </View>

            {/* Name */}
            <View style={styles.nameContainer}>
              <Text
                style={[
                  styles.userName,
                  { color: isCurrentUser ? colors.primary : colors.text },
                ]}
                numberOfLines={1}
              >
                {entry.userName}
                {isCurrentUser && " (jij)"}
              </Text>
            </View>

            {/* Score */}
            <Text style={[styles.score, { color: colors.primary }]}>
              {entry.bestScore.toLocaleString()}
            </Text>
          </TouchableOpacity>
        );
      })}

      {/* Show user's rank if not in top entries */}
      {myRank && myRank > maxEntries && (
        <View
          style={[
            styles.myRankRow,
            { backgroundColor: colors.primaryLight, borderTopColor: colors.border },
          ]}
        >
          <Text style={[styles.myRankText, { color: colors.primary }]}>
            Jouw positie: #{myRank}
          </Text>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    borderRadius: 12,
    overflow: "hidden",
    marginHorizontal: 16,
    marginBottom: 16,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    padding: 16,
    paddingBottom: 12,
  },
  headerTitle: {
    fontSize: 16,
    fontWeight: "bold",
    flex: 1,
  },
  badge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
  },
  badgeText: {
    fontSize: 11,
    fontWeight: "600",
  },
  emptyText: {
    fontSize: 14,
    fontWeight: "500",
    marginTop: 8,
  },
  emptySubtext: {
    fontSize: 12,
    marginTop: 4,
    textAlign: "center",
    paddingHorizontal: 20,
    paddingBottom: 16,
  },
  entry: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 12,
    paddingHorizontal: 16,
    gap: 12,
  },
  rankContainer: {
    width: 32,
    alignItems: "center",
  },
  rankText: {
    fontSize: 16,
    fontWeight: "bold",
  },
  avatar: {
    width: 36,
    height: 36,
    borderRadius: 18,
    justifyContent: "center",
    alignItems: "center",
    overflow: "hidden",
  },
  avatarImage: {
    width: "100%",
    height: "100%",
  },
  avatarText: {
    fontSize: 14,
    fontWeight: "bold",
  },
  nameContainer: {
    flex: 1,
  },
  userName: {
    fontSize: 15,
    fontWeight: "500",
  },
  score: {
    fontSize: 16,
    fontWeight: "bold",
  },
  myRankRow: {
    padding: 12,
    alignItems: "center",
    borderTopWidth: 1,
  },
  myRankText: {
    fontSize: 14,
    fontWeight: "600",
  },
});

export default Match3Leaderboard;

